import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HeartPulse, Save, Plus, Trash2, CheckCircle2, AlertTriangle, PhoneOff, Clock } from 'lucide-react';
import { careLineAPI } from '../services/api.js';
import { formatPhone } from '../lib/format.js';
import { COMMON_TIMEZONES } from '../lib/tz.js';
import { useAuth } from '../contexts/AuthContext.jsx';

// CARE LINE — scheduled well-being calls, and what happens when nobody answers.
//
// This is a family-care line, not a campaign: no dialer queue, no DNC scrubbing, no
// QA grading. Its job is to confirm a specific care task actually happened that day.
//
// Three things shape this screen:
//
//   1. The operator's real question is "did the check happen today?", NOT "what is
//      the interval set to". So TODAY'S STATE leads and the settings sit under it.
//      A care line you can configure but can't see the state of is the auto-pause
//      failure again — the platform acting with nothing on screen saying so.
//
//   2. A check is a CHAIN of times, not a call with a retry setting. Brian's model,
//      2026-07-30: "10:00. If not reached then 10:15. If not reached then 10:30."
//      The chain IS the bound — there is no separate interval or max-tries to get
//      wrong, and no unbounded reading of "until reached". Each chain carries its
//      own recurrence (daily / weekdays / weekly / monthly).
//
//      When the chain runs out, Alfred STOPS and a person is emailed. That is the
//      point, not a limitation: repeated dialling doesn't confirm the care happened,
//      someone finding out does. An 88-year-old called fifteen times in a morning
//      reads as a malfunction. Don't "fix" this by removing the end of the chain.
//
//   3. ⛔ THE TWO SWITCHES ARE NOT ONE SWITCH, and this screen must never merge them.
//      `enabled` runs the line (the evening report); `callsEnabled` arms the DIALER.
//      Core keeps them apart on purpose — the report can run for days before anyone
//      trusts an automated dialer with an 88-year-old. A single "on" control would
//      make one of the two invisible, and "the line is on" while nobody is being
//      called is exactly the state that went unnoticed from 07-24 to 07-31.
//
// ── CONTRACT — read it off core, not off the spec ────────────────────────────
// `core/src/routes/readiness.js` ("CARE LINE (Gary)") + `careLineService.js`.
//
//   GET  /tenants/:id/care-line → { data: { ...careLineRow, today, readback } }
//   PUT  /tenants/:id/care-line → { data: careLineRow }        (requireOwner)
//   400  { error:'bad_request',   issues: <zod flatten> }
//   422  { error:'invalid_config', issues: <string[]> }        (semantic refusals)
//   404  → no care line on this tenant (or the route isn't live). Card hides.
//
//   row:      { name, enabled, callsEnabled, did, destinations[], timezone,
//               slots: [{ key, purpose, times: ['10:00', …],
//                         repeat: { enabled, frequency, dayOfWeek?, dayOfMonth? } }],
//               escalationTo[], reportTo[], reportHour }
//   today:    { localDate, satisfied,
//               slots: [{ key, purpose, times, runsToday, firedAttempts[],
//                         due, dueAttemptIndex, dueAt, exhausted, escalatedAt? }] }
//   readback: [{ key, text }] — core's own plain-English sentence per chain.
//
// ⛔ The row is SPREAD into `data`; there is no `careLine` key and today's state is
// `today`, not `state`. An earlier revision of this card read `data.careLine` /
// `data.state`, so a tenant WITH a schedule rendered as "no schedule" on a 200 — a
// false negative on the one question the screen exists to answer. Likewise the error
// list is `issues`, not `errors`. If you change these, change them against the route.
//
// `escalatedAt` is optional and rendered only when core sends it (`todayState()` does
// not today) — see the ask in docs/channel-charlie-delta.md.

const FREQ_LABEL = {
  daily: 'Every day', weekdays: 'Every weekday', weekly: 'Weekly', monthly: 'Monthly'
};

const emailList = (v) => (Array.isArray(v) ? v : []).join(', ');
const parseEmails = (s) => s.split(',').map((x) => x.trim()).filter(Boolean);

/**
 * A comma-separated email field you can actually TYPE a comma into.
 *
 * ⛔ THE BUG THIS EXISTS TO FIX. The field used to render
 * `value={emailList(cfg.escalationTo)}` and `onChange={parseEmails(...)}`. Type a
 * comma after the first address and the round-trip is:
 *   "a@b.com,"  →  split/filter(Boolean)  →  ["a@b.com"]  →  join(', ')  →  "a@b.com"
 * The comma is deleted on the same keystroke that typed it, so a SECOND address was
 * literally unreachable — and this is the field that decides who gets told nobody
 * could reach an 88-year-old. Brian hit it: "it won't allow me to add a comma."
 *
 * Fix: the input owns its raw text while focused; the parsed array is what we store.
 * We only re-render from the array when the value changes underneath us (load/save),
 * never on our own keystrokes.
 */
function EmailListInput({ value, onChange, placeholder, id }) {
  const canonical = emailList(value);
  const [text, setText] = useState(canonical);
  const [focused, setFocused] = useState(false);
  // Re-sync from the server/parent only while the user isn't mid-edit.
  useEffect(() => { if (!focused) setText(canonical); }, [canonical, focused]);
  return (
    <input
      id={id}
      className="field-input"
      placeholder={placeholder}
      value={text}
      onFocus={() => setFocused(true)}
      onBlur={() => { setFocused(false); setText(emailList(parseEmails(text))); }}
      onChange={(e) => { setText(e.target.value); onChange(parseEmails(e.target.value)); }}
    />
  );
}

/**
 * Turn core's refusal into the sentence that tells the operator WHICH time it
 * refused. Both failure modes are keyed `issues`, but they are different shapes:
 *   422 invalid_config → string[] from careLineService.validateConfig
 *                        ("slots[0].times must ascend — '10:15' is not after '10:30'")
 *   400 bad_request    → zod .flatten() → { formErrors: [], fieldErrors: {…} }
 * Reading the wrong one leaves the banner showing the bare code `invalid_config`,
 * which is how a whole schedule gets retyped to find out what was wrong.
 */
function describeSaveError(err) {
  const issues = err.body?.issues;
  if (Array.isArray(issues) && issues.length) return issues.join(' · ');
  if (issues && typeof issues === 'object') {
    const flat = [
      ...(issues.formErrors || []),
      ...Object.entries(issues.fieldErrors || {}).map(([f, msgs]) => `${f}: ${[].concat(msgs).join(', ')}`)
    ].filter(Boolean);
    if (flat.length) return flat.join(' · ');
  }
  if (err.status === 403) return 'Only the account owner can change this schedule.';
  return err.body?.message || err.message || 'Save failed.';
}

/** Seed the next failover 15 minutes on. Core requires the chain to ASCEND, so an
 *  empty or duplicate seed makes the very next save fail on a field the operator
 *  hasn't touched yet. Clamps at 23:59 rather than rolling into the next day —
 *  a chain that wraps past midnight is a different day's check. */
function plusFifteen(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '').trim());
  if (!m) return '10:00';
  const total = Math.min(23 * 60 + 59, Number(m[1]) * 60 + Number(m[2]) + 15);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/** A slot key that cannot collide with an existing one — `slots.length + 1` does,
 *  the moment anything has been deleted, and core rejects the duplicate with a
 *  message about a `key` field this screen never shows. */
function nextSlotKey(slots = []) {
  const taken = new Set(slots.map((s) => s?.key).filter(Boolean));
  let n = slots.length + 1;
  while (taken.has(`check${n}`)) n += 1;
  return `check${n}`;
}

/**
 * @param {string}   tenantId
 * @param {string}   tenantName
 * @param {function} onStatus  called with { loading, unavailable, hasLine } so a host
 *                             page can explain an empty screen instead of showing one.
 *                             The card itself still renders nothing in those states,
 *                             which is what keeps it free on every other account.
 */
export default function CareLineEditor({ tenantId, tenantName, onStatus }) {
  const { isOwner } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [state, setState] = useState(null);
  // Core's own plain-English sentence per chain (describeSlot). Preferred over the
  // one this file can compose, so the screen and the worker cannot drift on what a
  // chain means — the read-back is the thing the operator trusts.
  const [readback, setReadback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);
  const [creating, setCreating] = useState(false);

  /**
   * Create the row so the editor has something to edit.
   *
   * ⛔ BOTH SWITCHES START OFF. This dials an 88-year-old man. Creating a schedule
   * and having it start ringing him because the defaults were "on" is not a risk
   * worth any convenience — `enabled` and `callsEnabled` are both false, and the
   * operator arms them deliberately once the times and numbers are right.
   * One 10:00 slot is seeded only because core's validator requires a slot with at
   * least one HH:MM time; it cannot fire while the switches are off.
   */
  const createLine = async () => {
    setCreating(true);
    setError('');
    try {
      await careLineAPI.update(tenantId, {
        name: tenantName ? `${tenantName} care line` : 'Care line',
        enabled: false,
        callsEnabled: false,
        destinations: [],
        timezone: 'America/New_York',
        slots: [{ key: 'morning', purpose: '', times: ['10:00'], repeat: { enabled: true, frequency: 'daily' } }],
        escalationTo: [],
        reportTo: [],
        reportHour: 18
      });
      await load();
    } catch (err) {
      // The write is what distinguishes "no row yet" from "route not deployed".
      setError(err.status === 404
        ? 'Scheduled calling isn’t available on this server yet — nothing was created.'
        : err.status === 403
          ? 'Only the account owner can set up a care line.'
          : describeSaveError(err));
    } finally {
      setCreating(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await careLineAPI.get(tenantId);
      // The row is SPREAD into `data` alongside `today` and `readback` — split them
      // apart so the config we edit (and PUT back) is only the row's own fields.
      const { today, readback: rb, ...row } = (r?.data || r || {});
      setCfg(row && row.id ? row : null);
      setState(today || null);
      setReadback(Array.isArray(rb) ? rb : []);
      setDirty(false);
      setUnavailable(false);
    } catch (err) {
      if (err.status === 404) setUnavailable(true);
      else setError(err.body?.message || err.message || 'Could not load the care line.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (tenantId) load(); }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tell the host page what happened, so a nav destination that resolves to nothing
  // can say why instead of rendering a blank page.
  useEffect(() => {
    onStatus?.({ loading, unavailable, hasLine: !!cfg });
  }, [loading, unavailable, cfg]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hidden entirely until core ships the endpoint, and hidden for tenants that
  // don't have a care line — this is not a feature every account should see.
  // ⛔ THERE WAS NO WAY TO CREATE ONE. The card hid itself whenever the GET 404'd,
  // and the GET 404s when the tenant has no `care_lines` row — so a line that had
  // never been configured could never BE configured from the dashboard. `care_lines`
  // sat at 0 rows with "needs a schedule configured" on the checklist and no screen
  // anywhere that could add one.
  //
  // Core returns the same `{error:'not_found'}` for "no row yet" and "route not
  // deployed", so we cannot tell them apart on the read. We don't try: we offer the
  // create, and the WRITE settles it — a 404 on the PUT means the service isn't
  // there, and that is what we say.
  if (unavailable || (!loading && !cfg)) {
    if (!isOwner) return null;
    return (
      <section className="card">
        <header className="mb-3 flex items-center gap-2">
          <HeartPulse size={18} className="text-alfred-gold" />
          <h2 className="text-xl text-stone-100">Care line</h2>
        </header>
        <p className="mb-4 text-sm text-stone-400">
          No scheduled calls are set up for {tenantName || 'this line'} yet. Creating one adds the
          schedule — it does <span className="text-stone-200">not</span> start calling anyone: both
          switches start off, and you arm them once the times and numbers are right.
        </p>
        {error && (
          <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>
        )}
        <button type="button" className="btn-primary" onClick={createLine} disabled={creating}>
          <Plus size={14} /> {creating ? 'Setting up…' : 'Set up scheduled calls'}
        </button>
      </section>
    );
  }

  // `dirty` gates core's saved read-back: once the operator edits, core's sentence
  // describes the OLD schedule and echoing it back would confirm something that
  // isn't what's on screen.
  const set = (patch) => { setDirty(true); setCfg((c) => ({ ...c, ...patch })); };
  const setSlot = (i, patch) => {
    setDirty(true);
    setCfg((c) => ({ ...c, slots: c.slots.map((s, n) => (n === i ? { ...s, ...patch } : s)) }));
  };
  const readbackFor = (key) => readback.find((r) => r.key === key)?.text || null;

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      // Send only the row's own fields. `id`/`tenantId`/timestamps are stripped by
      // zod anyway, but sending them back is how a read-only field eventually
      // becomes a writable one by accident.
      const { id, tenantId: _t, createdAt, updatedAt, ...payload } = cfg || {};
      await careLineAPI.update(tenantId, payload);
      // PUT returns only the row — no `today`. Re-read rather than keeping stale
      // state on screen: core normalises on write (drops empty destinations), and a
      // saved schedule that still shows what you typed is how a screen starts lying.
      await load();
      setSavedAt(new Date());
    } catch (err) {
      setError(describeSaveError(err));
    } finally {
      setSaving(false);
    }
  };

  // The chain IS the cap now (Brian, 2026-07-30) — there is no separate
  // intervalMin/maxTries. A slot's attempt budget is simply how many times are in it.
  const chainOf = (key) =>
    ((cfg?.slots || []).find((x) => x.key === key)?.times || []).filter(Boolean);

  const did = (cfg?.did || '').trim();
  // Core rejects this on save; say it here so a whole schedule isn't retyped to find out.
  const didIsDestination = !!did && (cfg?.destinations || []).some((d) => String(d).trim() === did);
  const liveDestinations = (cfg?.destinations || []).filter((d) => d && d.trim());

  return (
    <section className="card">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <HeartPulse size={18} className="text-alfred-gold" />
          <h2 className="text-xl text-stone-100">Care line</h2>
        </div>
        {savedAt && <span className="text-xs text-stone-500">Saved {savedAt.toLocaleTimeString()}</span>}
      </header>

      <p className="mb-4 text-sm text-stone-400">
        Scheduled well-being calls{tenantName ? ` for ${tenantName}` : ''}
        {liveDestinations.length
          ? <> to {liveDestinations.map(formatPhone).join(' and ')}</>
          : ''}.
        Each check is a chain of times: if he isn&rsquo;t reached at the first, Alfred tries the next.
        When the chain runs out it stops and emails the people you list.
      </p>

      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>
      )}

      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : (
        <>
          {/* ── TODAY leads. The question is "did it happen", not "what's configured". */}
          {state?.slots?.length > 0 && (
            <div className="mb-6">
              <h3 className="mb-2 text-sm uppercase tracking-wide text-stone-500">
                Today{state.localDate ? ` — ${state.localDate}` : ''}
              </h3>
              <ul className="space-y-2">
                {state.slots.map((s) => {
                  const cfgSlot = (cfg?.slots || []).find((x) => x.key === s.key);
                  const label = cfgSlot?.purpose || s.purpose || s.key;
                  // Satisfied is reported for the DAY: any real conversation, in
                  // either direction, cancels every remaining chain (careLineService
                  // nextDueAttempt). That IS the worker's rule, so the card must not
                  // invent per-slot state core doesn't have — but see `cancelled`.
                  const satisfied = !!state.satisfied;
                  const tried = (s.firedAttempts || []).length;
                  const chain = chainOf(s.key).length || (s.times || []).length;
                  const escalated = !!s.escalatedAt || (!satisfied && s.exhausted);
                  // ⛔ SATISFIED-BUT-NEVER-RAN is not the same as CONFIRMED, and must
                  // not wear the same green tick. If he rings in at 08:40 about a
                  // bill, the day is satisfied and the 15:00 "second drink + changed"
                  // check is cancelled having never been asked. Showing that as
                  // "answered" would tell the family a care task was confirmed when
                  // nobody ever raised it — the one lie this screen cannot afford.
                  const cancelled = satisfied && tried === 0;
                  const Icon = cancelled ? Clock
                    : satisfied ? CheckCircle2
                      : escalated ? PhoneOff
                        : tried ? Clock : AlertTriangle;
                  const tone = cancelled ? 'text-stone-400'
                    : satisfied ? 'text-emerald-300'
                      : escalated ? 'text-rose-300'
                        : tried ? 'text-amber-300' : 'text-stone-400';
                  return (
                    <li key={s.key} className="flex items-start gap-2 text-sm">
                      <Icon size={15} className={`mt-0.5 shrink-0 ${tone}`} />
                      <div className="min-w-0">
                        <span className="text-stone-200 capitalize">{s.key}</span>
                        <span className="text-stone-500"> — {label}</span>
                        <div className="text-xs text-stone-500">
                          {cancelled
                            ? 'Cancelled — he was reached earlier today, so this check was never asked.'
                            : satisfied
                              ? 'He was reached on this check. Nothing further today.'
                              : s.runsToday === false
                              ? 'Not scheduled today.'
                              : escalated
                                ? `Not reached after ${tried} of ${chain || '—'} ${tried === 1 ? 'try' : 'tries'} — the chain is finished${s.escalatedAt ? ' and a message went out' : ''}.`
                                : tried
                                  ? `${tried} of ${chain || '—'} times tried so far.`
                                  : s.due
                                    ? `Due now${s.dueAt ? ` (${s.dueAt})` : ''}.`
                                    : `Not due yet${(s.times || [])[0] ? ` — first call at ${(s.times || [])[0]}` : ''}.`}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="space-y-4">
            {/* ── The two switches. See note 3 in the header — they are separate on
                   purpose and must stay separate on screen. ── */}
            <div className="space-y-2 rounded-lg border border-stone-800 bg-stone-900/40 p-3">
              <label className="flex items-start gap-2 text-sm text-stone-300">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={cfg?.enabled !== false}
                  onChange={(e) => set({ enabled: e.target.checked })}
                />
                <span>
                  Run this care line
                  <span className="block text-xs text-stone-500">
                    Watches the day and emails the evening report. Off = nothing happens at all.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-stone-300">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={!!cfg?.callsEnabled}
                  onChange={(e) => set({ callsEnabled: e.target.checked })}
                />
                <span>
                  Let Alfred place the scheduled calls
                  <span className="block text-xs text-stone-500">
                    Arms the dialer. With this off the schedule below is recorded but{' '}
                    <span className="text-stone-300">no call is ever placed</span> — the report still sends.
                  </span>
                </span>
              </label>
              {cfg?.enabled !== false && !cfg?.callsEnabled && (
                <p className="flex items-start gap-2 rounded-lg border border-amber-600/40 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  The line is on but the dialer is not armed — the checks below will never ring.
                </p>
              )}
            </div>

            {/* ── The line itself. Both fields are in core's schema and neither had a
                   control, so a care line was stuck on whatever the seed wrote: no
                   caller ID could ever be set, and the clock could not be moved off
                   the America/New_York default. Every time on this screen is read in
                   THIS zone, so it is not a detail. ── */}
            <div className="flex flex-wrap items-end gap-3">
              <label className="block flex-1 min-w-[220px]">
                <span className="field-label">Calls come from</span>
                <input
                  className="field-input"
                  placeholder="+18652730436"
                  value={cfg?.did || ''}
                  onChange={(e) => set({ did: e.target.value.trim() })}
                />
                <span className="mt-1 block text-xs text-stone-500">
                  Our line — the caller ID he sees. Never one of the numbers below.
                </span>
              </label>
              <label className="block">
                <span className="field-label">Times are in</span>
                <select
                  className="field-input w-64"
                  value={cfg?.timezone || 'America/New_York'}
                  onChange={(e) => set({ timezone: e.target.value })}
                >
                  {/* Keep an unlisted zone selectable rather than silently rewriting
                      it to Eastern the next time anyone opens this screen. */}
                  {!COMMON_TIMEZONES.some((z) => z.value === (cfg?.timezone || 'America/New_York')) && (
                    <option value={cfg.timezone}>{cfg.timezone}</option>
                  )}
                  {COMMON_TIMEZONES.map((z) => (
                    <option key={z.value} value={z.value}>{z.label}</option>
                  ))}
                </select>
              </label>
            </div>

            {/* ── His numbers. ORDERED, and the order is the retry order: a retry
                   that redials the same handset every 15 minutes mostly proves that
                   handset isn't near him. ── */}
            <div>
              <h3 className="mb-2 text-sm uppercase tracking-wide text-stone-500">Numbers to try</h3>
              <ul className="space-y-2">
                {(cfg?.destinations || []).map((num, i) => (
                  <li key={i} className="flex flex-wrap items-end gap-2">
                    <label className="block flex-1 min-w-[220px]">
                      <span className="field-label text-xs">
                        {i === 0 ? 'First' : `Then`}
                      </span>
                      <input
                        className="field-input text-sm"
                        placeholder="+18655183879"
                        value={num}
                        onChange={(e) => set({
                          destinations: cfg.destinations.map((d, n) => (n === i ? e.target.value.trim() : d))
                        })}
                      />
                    </label>
                    <button
                      type="button"
                      className="btn-ghost text-red-300 hover:text-red-200"
                      onClick={() => set({ destinations: cfg.destinations.filter((_, n) => n !== i) })}
                      aria-label="Remove this number"
                    >
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className="btn-ghost mt-2"
                onClick={() => set({ destinations: [...(cfg?.destinations || []), ''] })}
              >
                <Plus size={13} /> Add a number
              </button>
              <p className="mt-1 text-xs text-stone-500">
                Tried in this order, and each retry moves to the next one — so a second number is
                worth more than a second attempt on the same handset.
                {did && <> Calls show as coming from <span className="text-stone-300">{formatPhone(did)}</span>.</>}
              </p>
              {liveDestinations.length === 0 && (
                <p className="mt-2 flex items-start gap-2 rounded-lg border border-amber-600/40 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  No number to call. The scheduled checks below can&rsquo;t run.
                </p>
              )}
              {didIsDestination && (
                <p className="mt-2 flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  {formatPhone(did)} is this line&rsquo;s own number. Calling it dials the platform, not a
                  person — remove it from the list above.
                </p>
              )}
            </div>

            {/* ── The CHAINS. Brian's model, 2026-07-30: a check is a primary time
                   plus an ordered chain of FAILOVER times. If the preceding time
                   doesn't reach him, the next one in the chain is attempted. Each
                   chain has its own recurrence. ── */}
            <div>
              <h3 className="mb-2 text-sm uppercase tracking-wide text-stone-500">Scheduled checks</h3>
              <ul className="space-y-4">
                {(cfg?.slots || []).map((slot, i) => {
                  const times = slot.times?.length ? slot.times : ['10:00'];
                  const rep = slot.repeat || {};
                  return (
                    <li key={slot.key || i} className="rounded-lg border border-stone-800 bg-stone-900/40 p-3">
                      <div className="mb-3 flex flex-wrap items-end gap-2">
                        <label className="block flex-1 min-w-[220px]">
                          {/* ⛔ THIS TEXT NEVER REACHES THE CALL. `slot.purpose` is used in
                              exactly two places server-side: this screen, and the subject +
                              body of the "could not reach him" email (garyCareWorker.js:225).
                              It is NOT passed to placeCall and never enters a prompt.
                              Brian typed "Need to follow the normal checklist." here expecting
                              it to instruct Alfred — it can't, and the old label
                              ("What this check confirms") actively invited that reading.
                              What Alfred actually says lives in Settings → the assistant
                              instructions. Name the field for what it is and point at the
                              place that does work. */}
                          <span className="field-label text-xs">Name this check (for you and the alert email)</span>
                          <input
                            className="field-input text-sm"
                            placeholder="e.g. Morning check"
                            value={slot.purpose || ''}
                            onChange={(e) => setSlot(i, { purpose: e.target.value })}
                          />
                        </label>
                        <button
                          type="button"
                          className="btn-ghost text-red-300 hover:text-red-200"
                          onClick={() => set({ slots: cfg.slots.filter((_, n) => n !== i) })}
                          aria-label="Remove this check"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>

                      {/* THE CHAIN. times[0] is the real appointment; the rest only
                          happen if the one before them didn't reach him. */}
                      <ul className="space-y-2">
                        {times.map((t, ti) => (
                          <li key={ti} className="flex flex-wrap items-center gap-2">
                            <span className="w-28 shrink-0 text-xs text-stone-500">
                              {ti === 0 ? 'Call at' : 'If no answer'}
                            </span>
                            <input
                              type="time"
                              className="field-input w-32 text-sm"
                              value={t}
                              onChange={(e) => setSlot(i, {
                                times: times.map((x, n) => (n === ti ? e.target.value : x))
                              })}
                            />
                            {ti > 0 && (
                              <button
                                type="button"
                                className="btn-ghost text-red-300 hover:text-red-200"
                                onClick={() => setSlot(i, { times: times.filter((_, n) => n !== ti) })}
                                aria-label="Remove this failover time"
                              >
                                <Trash2 size={13} />
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                      <button
                        type="button"
                        className="btn-ghost mt-2"
                        onClick={() => setSlot(i, { times: [...times, plusFifteen(times[times.length - 1])] })}
                      >
                        <Plus size={13} /> Add a fallback time
                      </button>

                      {/* Recurrence, per chain. */}
                      <div className="mt-3 flex flex-wrap items-end gap-3 border-t border-stone-800 pt-3">
                        <label className="flex items-center gap-2 text-sm text-stone-300">
                          <input
                            type="checkbox"
                            checked={rep.enabled !== false}
                            onChange={(e) => setSlot(i, { repeat: { ...rep, enabled: e.target.checked } })}
                          />
                          Repeat
                        </label>
                        {rep.enabled !== false && (
                          <label className="block">
                            <span className="field-label text-xs">How often</span>
                            <select
                              className="field-input w-44 text-sm"
                              value={rep.frequency || 'daily'}
                              onChange={(e) => setSlot(i, { repeat: { ...rep, frequency: e.target.value } })}
                            >
                              <option value="daily">Every day</option>
                              <option value="weekdays">Weekdays only</option>
                              <option value="weekly">Weekly</option>
                              <option value="monthly">Monthly</option>
                            </select>
                          </label>
                        )}
                      </div>

                      {/* State the chain back in one sentence — a list of time inputs
                          doesn't tell you what will actually happen. */}
                      {/* Read-back. Prefer CORE's sentence (describeSlot, returned as
                          `readback`) so the screen and the worker cannot describe the
                          same chain differently — but only while the row is clean,
                          since core's copy describes what was SAVED, not what is
                          currently typed. Once edited, fall back to composing it here
                          and say so implicitly by matching the saved wording. */}
                      {times.filter(Boolean).length > 0 && (
                        <p className="mt-2 text-xs text-stone-400">
                          {!dirty && readbackFor(slot.key)
                            ? readbackFor(slot.key)
                            : <>
                                {rep.enabled === false
                                  ? 'Once: '
                                  : `${FREQ_LABEL[rep.frequency || 'daily']}: `}
                                calls at <span className="text-stone-200">{times[0]}</span>
                                {times.length > 1
                                  ? <>, and if he doesn&rsquo;t answer, again at{' '}
                                      <span className="text-stone-200">{times.slice(1).filter(Boolean).join(' then ')}</span>.
                                      After that Alfred stops and emails the people below.</>
                                  : <>. No fallback time set — if he doesn&rsquo;t answer, Alfred stops there.</>}
                              </>}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
              <button
                type="button"
                className="btn-ghost mt-3"
                onClick={() => set({
                  slots: [...(cfg?.slots || []), {
                    key: nextSlotKey(cfg?.slots),
                    purpose: '',
                    // Seed a real time, not '' — an empty string fails core's HH:MM
                    // regex, so a half-filled new check refuses the whole save.
                    times: ['10:00'],
                    repeat: { enabled: true, frequency: 'daily' }
                  }]
                })}
              >
                <Plus size={13} /> Add another check
              </button>
              <p className="mt-1 text-xs text-stone-400">
                <span className="text-stone-200">These times decide WHEN Alfred calls, not what he says.</span>{' '}
                What he asks about — the daily check-in items, his wife’s care, everything he works
                into the conversation — comes from the assistant instructions in{' '}
                <Link to="/settings" className="text-alfred-gold hover:underline">Settings</Link>.
                Editing a name here changes the label on this screen and in the alert email; it does
                not change a word of the call.
              </p>
              <p className="mt-1 text-xs text-stone-500">
                Times are in {cfg?.timezone || 'the line’s own timezone'}. A check counts as done the
                moment there&rsquo;s a real conversation — including one he starts himself — and the rest
                of its chain is dropped.
              </p>
            </div>

            {/* ── Escalation: the point of the cap ── */}
            <div>
              <label className="block">
                <span className="field-label">Tell these people when he can’t be reached</span>
                <EmailListInput
                  placeholder="brian@muleworks.co, sherriword@gmail.com"
                  value={cfg?.escalationTo}
                  onChange={(next) => set({ escalationTo: next })}
                />
                <span className="mt-1 block text-xs text-stone-500">
                  Comma-separated. Sent once per missed check — not once per attempt.
                </span>
              </label>
              {(cfg?.escalationTo || []).length === 0 && (
                <p className="mt-2 flex items-start gap-2 rounded-lg border border-amber-600/40 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  Nobody is listed. If a check goes unanswered, Alfred will stop calling and no one
                  will be told — which is the situation this line exists to prevent.
                </p>
              )}
            </div>

            {/* ── The evening report. Separate from escalation: escalation is the
                   alarm, this is the daily read-out, and a day with NO contact still
                   sends one — silence is itself the report. ── */}
            <div className="flex flex-wrap items-end gap-3">
              <label className="block flex-1 min-w-[260px]">
                <span className="field-label">Daily report goes to</span>
                <EmailListInput
                  placeholder="brian@muleworks.co, sherriword@gmail.com"
                  value={cfg?.reportTo}
                  onChange={(next) => set({ reportTo: next })}
                />
                <span className="mt-1 block text-xs text-stone-500">
                  A summary of the day&rsquo;s conversations. A day with no contact still sends, and says so.
                </span>
              </label>
              <label className="block">
                <span className="field-label">Sent at</span>
                <select
                  className="field-input w-32"
                  value={Number.isInteger(cfg?.reportHour) ? cfg.reportHour : 18}
                  onChange={(e) => set({ reportHour: Number(e.target.value) })}
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>
                      {h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* PUT is requireOwner (readiness.js). Don't offer a save that 403s —
                let the reader see the schedule and know why they can't change it. */}
            <button type="button" className="btn-primary" onClick={save} disabled={saving || !isOwner}>
              <Save size={14} /> {saving ? 'Saving…' : 'Save care line'}
            </button>
            {!isOwner && (
              <p className="mt-2 text-xs text-stone-500">
                Read-only — only the account owner can change this schedule.
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
}
